import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { 
  BarChart3, 
  TrendingUp,
  Users,
  BookOpen,
  Target,
  Calendar,
  Download,
  AlertCircle,
  Lock,
  Sparkles,
  AlertTriangle,
  ArrowUpRight,
  Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function SchoolAnalytics() {
  const { admin, school } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [gradeStats, setGradeStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("30");
  const [analyticsLevel, setAnalyticsLevel] = useState("basic");
  const [levelFeatures, setLevelFeatures] = useState({});

  const permissions = admin?.permissions || [];
  const canView = permissions.includes("view_analytics");

  useEffect(() => {
    if (canView) {
      fetchAnalyticsLevel();
      fetchAnalytics();
    } else {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (canView) {
      fetchAnalytics();
    }
  }, [selectedGrade, selectedPeriod]);

  const fetchAnalyticsLevel = async () => {
    try {
      const response = await fetch(`${API}/school/analytics-level`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyticsLevel(data.analytics_level || "basic");
        setLevelFeatures(data.features || {});
      }
    } catch (error) {
      console.error("Error fetching analytics level:", error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedGrade && selectedGrade !== "all") params.append("grade", selectedGrade);
      params.append("days", selectedPeriod);

      const response = await fetch(`${API}/school/analytics/dashboard?${params}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
        setGradeStats(data.grade_stats || []);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Locked feature card component
  const LockedFeatureCard = ({ title, description, requiredPlan }) => (
    <Card className="border-0 shadow-sm bg-slate-50/50 relative overflow-hidden">
      <div className="absolute inset-0 backdrop-blur-[1px] bg-white/60 z-10 flex items-center justify-center">
        <div className="text-center p-4">
          <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-600">Upgrade to {requiredPlan}</p>
          <p className="text-xs text-slate-500">to unlock this feature</p>
        </div>
      </div>
      <CardHeader className="opacity-40">
        <CardTitle className="text-sm">{title}</CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent className="opacity-40">
        <div className="h-24 bg-slate-100 rounded animate-pulse"></div>
      </CardContent>
    </Card>
  );

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Restricted</h2>
        <p className="text-slate-500">You don't have permission to view analytics.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  const isDetailed = analyticsLevel === "detailed" || analyticsLevel === "advanced";
  const isAdvanced = analyticsLevel === "advanced";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-800">Analytics</h1>
            <Badge 
              variant="outline" 
              className={`text-xs ${
                isAdvanced ? 'border-purple-300 text-purple-600 bg-purple-50' :
                isDetailed ? 'border-blue-300 text-blue-600 bg-blue-50' :
                'border-slate-300 text-slate-600'
              }`}
            >
              {isAdvanced && <Crown className="w-3 h-3 mr-1" />}
              {analyticsLevel.charAt(0).toUpperCase() + analyticsLevel.slice(1)} Analytics
            </Badge>
          </div>
          <p className="text-slate-500 text-sm">Track learning progress and engagement</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedGrade} onValueChange={setSelectedGrade}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Grades" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Grades</SelectItem>
              {(school?.grades_offered || [5, 6, 7, 8]).map(g => (
                <SelectItem key={g} value={g.toString()}>Grade {g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          {isAdvanced ? (
            <Button variant="outline" data-testid="export-analytics-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          ) : (
            <Button variant="outline" disabled className="opacity-50">
              <Lock className="w-4 h-4 mr-2" />
              Export (Pro)
            </Button>
          )}
        </div>
      </div>

      {/* Overview Stats - Available for ALL tiers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {analytics?.active_students || 0}
                </p>
                <p className="text-sm text-slate-500">Active Students</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {analytics?.total_questions || 0}
                </p>
                <p className="text-sm text-slate-500">Questions Answered</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {analytics?.avg_accuracy?.toFixed(1) || 0}%
                </p>
                <p className="text-sm text-slate-500">Avg Accuracy</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {analytics?.avg_streak || 0}
                </p>
                <p className="text-sm text-slate-500">Avg Streak</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grade-wise Performance - Available for ALL tiers */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Grade-wise Performance
          </CardTitle>
          <CardDescription>Learning progress by grade</CardDescription>
        </CardHeader>
        <CardContent>
          {gradeStats.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No data available yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {gradeStats.map((grade) => (
                <div key={grade.grade} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-slate-700">Grade {grade.grade}</span>
                      <span className="text-sm text-slate-500">
                        {grade.active_students} active students
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-emerald-600">
                        {grade.avg_accuracy?.toFixed(1) || 0}%
                      </span>
                      <span className="text-sm text-slate-500 ml-2">accuracy</span>
                    </div>
                  </div>
                  <Progress 
                    value={grade.avg_accuracy || 0} 
                    className="h-3"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{grade.total_questions || 0} questions answered</span>
                    <span>Avg streak: {grade.avg_streak || 0} days</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subject Performance - DETAILED & ADVANCED only */}
      {isDetailed ? (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Subject Performance
              <Badge variant="outline" className="text-xs border-blue-200 text-blue-600 bg-blue-50 ml-2">
                Plus+
              </Badge>
            </CardTitle>
            <CardDescription>Accuracy breakdown across subjects</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.subject_stats?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {analytics.subject_stats.map((subject) => (
                  <div key={subject.name} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-700 capitalize">{subject.name}</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {subject.accuracy?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <Progress value={subject.accuracy || 0} className="h-2" />
                    <p className="text-xs text-slate-500 mt-2">
                      {subject.questions || 0} questions
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No subject data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <LockedFeatureCard 
          title="Subject Performance" 
          description="Detailed accuracy breakdown across subjects"
          requiredPlan="School Plus"
        />
      )}

      {/* Trend Charts - DETAILED & ADVANCED only */}
      {isDetailed ? (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Engagement Trends
              <Badge variant="outline" className="text-xs border-blue-200 text-blue-600 bg-blue-50 ml-2">
                Plus+
              </Badge>
            </CardTitle>
            <CardDescription>Daily active students and questions over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-600">Weekly Activity</span>
                  <ArrowUpRight className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex items-end gap-2 h-24">
                  {[40, 65, 45, 80, 55, 75, 90].map((h, i) => (
                    <div key={i} className="flex-1 bg-green-200 rounded-t" style={{ height: `${h}%` }}></div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-600">Accuracy Trend</span>
                  <ArrowUpRight className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-end gap-2 h-24">
                  {[55, 60, 58, 72, 68, 75, 78].map((h, i) => (
                    <div key={i} className="flex-1 bg-blue-200 rounded-t" style={{ height: `${h}%` }}></div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <LockedFeatureCard 
          title="Engagement Trends" 
          description="Activity and accuracy trends over time"
          requiredPlan="School Plus"
        />
      )}

      {/* Advanced Analytics Section - ADVANCED only */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Struggling Topics - ADVANCED only */}
        {isAdvanced ? (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Struggling Topics
                <Badge variant="outline" className="text-xs border-purple-200 text-purple-600 bg-purple-50 ml-2">
                  Pro
                </Badge>
              </CardTitle>
              <CardDescription>Topics where students need more help</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(analytics?.struggling_topics || [
                  { topic: "Algebra - Linear Equations", error_rate: 45, subject: "Mathematics" },
                  { topic: "Photosynthesis", error_rate: 38, subject: "Science" },
                  { topic: "Direct & Indirect Speech", error_rate: 32, subject: "English" },
                ]).slice(0, 5).map((topic, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div>
                      <p className="font-medium text-slate-700 text-sm">{topic.topic}</p>
                      <p className="text-xs text-slate-500">{topic.subject}</p>
                    </div>
                    <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-100">
                      {topic.error_rate}% errors
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <LockedFeatureCard 
            title="Struggling Topics" 
            description="Identify topics where students need more help"
            requiredPlan="School Pro"
          />
        )}

        {/* Predictive Insights - ADVANCED only */}
        {isAdvanced ? (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                AI Insights
                <Badge variant="outline" className="text-xs border-purple-200 text-purple-600 bg-purple-50 ml-2">
                  Pro
                </Badge>
              </CardTitle>
              <CardDescription>AI-powered recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">📈 Engagement Alert:</span> Grade 7 shows 23% lower activity this week. Consider sending a reminder.
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">⭐ Top Performer:</span> Grade 6 Mathematics has the highest accuracy (85%). Great job!
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">💡 Suggestion:</span> Schedule extra practice sessions for Algebra topics in Grade 7.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <LockedFeatureCard 
            title="AI Insights" 
            description="AI-powered analytics and recommendations"
            requiredPlan="School Pro"
          />
        )}
      </div>

      {/* Upgrade Banner for Basic tier */}
      {analyticsLevel === "basic" && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-lg">Unlock More Insights</h3>
                <p className="text-emerald-100 text-sm">
                  Upgrade to School Plus or Pro for detailed analytics, trend charts, struggling topics, and AI insights.
                </p>
              </div>
              <Button variant="secondary" className="bg-white text-emerald-600 hover:bg-emerald-50">
                View Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
